import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
    deleteManyOutcome,
    deleteTargetText,
    type DeleteManyResult,
    type DeleteTarget,
} from '../components/Selection/selectionSummary.js';
import type { EntityType } from '../domain/entityTypes.js';
import { countWord } from '../domain/pluralise.js';
import { errorMessage } from '../utils/errors.js';
import { refetchAfterDelete, shouldInvalidate } from '../utils/invalidation.js';
import { ENTITY_CALLS } from './entityCalls.js';
import { useTRPCClient } from '../utils/trpc.js';

export type DeleteMasterArgs = {
    type: EntityType;
    key: string;
    expectedRevision: number;
    title: string | null;
    /** A studio's master game count, for the scope sentence; null for a game. */
    childCount: number | null;
    onDeleted: () => void;
};

/**
 * A delete's own invalidation, in place of `App.tsx`'s (`skipInvalidate`): everything is marked
 * stale, but `refetchAfterDelete` decides what refetches now.
 */
const invalidateAfterDelete = (queryClient: QueryClient): void => {
    void queryClient.invalidateQueries({ predicate: (q) => refetchAfterDelete(q.queryKey) });
    void queryClient.invalidateQueries({
        predicate: (q) => shouldInvalidate(q.queryKey) && !refetchAfterDelete(q.queryKey),
        refetchType: 'none',
    });
};

/** Delete from the hub's master only; `confirmDeleteMaster` asks first. */
export const useDeleteMaster = () => {
    const client = useTRPCClient();
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: (args: DeleteMasterArgs) =>
            ENTITY_CALLS[args.type].deleteMaster(client, args.key, args.expectedRevision),
        meta: { skipInvalidate: true },
        onSuccess: (result, variables) => {
            invalidateAfterDelete(queryClient);
            notifications.show({
                color: 'gray',
                title: 'Deleted from master',
                message: `${countWord(result.deleted, 'master record')} deleted. Environment records were not changed.`,
            });
            variables.onDeleted();
        },
        // A descendant batch may have landed before a later failure: refresh so the matrix never
        // keeps showing records already gone.
        onError: () => invalidateAfterDelete(queryClient),
    });
    return { run: (args: DeleteMasterArgs) => mutation.mutate(args), isPending: mutation.isPending };
};

/**
 * The selection bar's delete: each target from master only, one after another, each with its own
 * revision guard and audit row. One mutation, so the matrix is refreshed once at the end; a
 * target that fails does not stop the rest, and the notification names it.
 */
export const useDeleteMasterMany = () => {
    const client = useTRPCClient();
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async ({ targets }: { targets: DeleteTarget[]; onDone: () => void }): Promise<DeleteManyResult> => {
            const result: DeleteManyResult = { deleted: 0, failed: [] };
            for (const target of targets) {
                try {
                    const { deleted } = await ENTITY_CALLS[target.type].deleteMaster(
                        client,
                        target.key,
                        target.expectedRevision,
                    );
                    result.deleted += deleted;
                } catch (error) {
                    result.failed.push({ label: deleteTargetText(target), message: errorMessage(error) });
                }
            }
            return result;
        },
        meta: { skipInvalidate: true },
        onSuccess: (result, variables) => {
            invalidateAfterDelete(queryClient);
            notifications.show(deleteManyOutcome(result));
            variables.onDone();
        },
    });
    return {
        run: (targets: DeleteTarget[], onDone: () => void) => mutation.mutate({ targets, onDone }),
        isPending: mutation.isPending,
    };
};
